import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import PubSub from "../services/PubSub";
import User from "../services/user";

const theme = createTheme();

const ApplicationServicesContext = createContext({});

export const ApplicationServicesProvider = ({ children }) => {
    const [user, _setUser] = useState(() => {
        const stored = window.sessionStorage.getItem("user") || "{}";
        return JSON.parse(stored) || {};
    });
    const setUser = useCallback((newUser) => {
        const userToStore = {
            _id: newUser._id,
            email: newUser.email,
            appAdmin: newUser.appAdmin,
            firstName: newUser.firstName,
            lastName: newUser.lastName,
            adminForDomains: newUser.adminForDomains,
            uiPermissions: newUser.uiPermissions,
            singleDomainUser: newUser.singleDomainUser,
            trainingsCompleted: newUser.trainingsCompleted
        };
        window.sessionStorage.setItem("user", JSON.stringify(userToStore));
        _setUser(userToStore);
    }, []);

    const refreshUser = useCallback(async () => {
        const updatedUser = await User.getUser();
        if (updatedUser && !updatedUser.error) {
            setUser(updatedUser.data);
        }
    }, [setUser]);

    const [domain, _setDomain] = useState(() => {
        const stored = window.sessionStorage.getItem("domain") || "{}";
        return JSON.parse(stored);
    });
    const setDomain = useCallback((newDomain) => {
        window.sessionStorage.setItem("domain", JSON.stringify(newDomain));
        _setDomain(newDomain);
    }, []);

    const [isRootDomain, _setIsRootDomain] = useState(() => {
        return window.sessionStorage.getItem("root-domain") === "true";
    });
    const setIsRootDomain = useCallback((value) => {
        window.sessionStorage.setItem("root-domain", value);
        _setIsRootDomain(value === true || value === "true");
    }, []);

    const handleLogout = useCallback((navigate) => {
        PubSub.publish('hide-deployment-toasts');
        window.sessionStorage.clear();
        _setUser({});
        _setDomain({});
        _setIsRootDomain(false);
        navigate("/auth");
    }, []);

    useEffect(() => {
        const id = PubSub.subscribe("updated-user", () => {
            _setUser(JSON.parse(window.sessionStorage.getItem("user") || "{}") || {});
        });
        return () => PubSub.unsubscribe("updated-user", id);
    }, []);

    const value = {
        user,
        setUser,
        refreshUser,
        domain,
        setDomain,
        isRootDomain,
        setIsRootDomain,
        handleLogout,
    };

    return (
        <ApplicationServicesContext.Provider value={value}>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    {children}
                </ThemeProvider>
            </StyledEngineProvider>
        </ApplicationServicesContext.Provider>
    );
};

export const useAppContext = () => {
    return useContext(ApplicationServicesContext);
};

export default ApplicationServicesContext;